import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { Button } from '@cottage/design-system/internal-tooling/ui'
import { HoverHelp } from '@/tableControls'
import {
  computeWaterfallSchedule,
  parseApiCashPolicySplitSchedule,
  formatApiError,
  getDistributionHistory,
  getYieldCurve,
  waterfallScheduleRequestFromPolicy,
  type DistributionHistoryResponse,
  type FloorAnalysisResponse,
  type ModelSummaryResponse,
} from '@/services/projections'
import { toast } from '@/store/toastStore'
import {
  evaluateContextFromModel,
  thresholdsFromSettings,
  validationOptionsFromModel,
} from '@/store/projectionsStore'
import { evaluate, parseNumeric } from '../engine'
import { scheduleIsValid } from '../engine/cashPolicy'
import { hydrateCashPolicy } from './hydratePolicy'
import type { CaseInputs, CashPolicy, History, Model, SchemaEntry, ValidationIssue } from '../engine/types'
import { formatCurrency } from '../projectionsFormat'
import { validateCase } from '../projectionsValidation'
import { VALIDATION_CODES } from '../validationCodes'
import type { FloorAnalysisWindowMonths } from './FloorAnalysisPanel'
import { getCachedFloorAnalysis, loadFloorAnalysis } from './floorAnalysisCache'
import PolicyChart from './PolicyChart'
import PolicyInputsPanel from './PolicyInputsPanel'
import PolicyMonthTable from './PolicyMonthTable'
import PolicySummaryTiles from './PolicySummaryTiles'

export type CashPolicyModalProps = {
  open: boolean
  canEdit: boolean
  caseName: string
  draft: CaseInputs
  storeDirty: boolean
  schema: SchemaEntry[]
  history: History
  model: ModelSummaryResponse
  lastClosedMonth: string
  onClose: () => void
  onSetCashPolicy: (policy: CashPolicy | null) => void
}

function cashPolicySettings(model: ModelSummaryResponse): Record<string, unknown> {
  return (model.settings?.cash_policy ?? {}) as Record<string, unknown>
}

export function defaultCashPolicy(
  model: ModelSummaryResponse,
  history: History,
  distHistory: DistributionHistoryResponse,
): CashPolicy {
  const settings = cashPolicySettings(model)
  const yieldViewDefault = (settings.yield_income_view_default as CashPolicy['sweep']['yield_income_view']) ?? 'ga'
  const operatingMinimumDefault =
    settings.operating_minimum_default != null
      ? Number(settings.operating_minimum_default)
      : 0
  const rawOpening = history.values.consolidated?.['bs.cash_equivalents']?.[history.nHistory - 1]
  const parsedOpening = typeof rawOpening === 'string' ? parseNumeric(rawOpening) : rawOpening
  const opening = typeof parsedOpening === 'number' ? parsedOpening : 0
  const avgAmount = distHistory.avg_amount_last_6 ? Number(distHistory.avg_amount_last_6) : 0
  return {
    enabled: true,
    floor: {
      operating_minimum: Number.isFinite(operatingMinimumDefault) ? operatingMinimumDefault : 0,
      drawdown_allowance: 0,
    },
    distribution: {
      cadence: 'biweekly',
      anchor_date: distHistory.last_date ?? '2026-09-11',
      amount_per_period: avgAmount,
      one_time: null,
      split_schedule: null,
    },
    sweep: {
      enabled: true,
      mode: 'fixed',
      amount_per_period: null,
      yield_snapshot: null,
      yield_income_view: yieldViewDefault,
      opening_mm_balance: opening,
    },
    solver: { horizon_months: 36, round_to: 500 },
  }
}

function policiesEqual(a: CashPolicy | null, b: CashPolicy | null): boolean {
  return JSON.stringify(a) === JSON.stringify(b)
}

export function collapseFloorBreachWarnings(items: ValidationIssue[]): ValidationIssue[] {
  const floorBreaches = items.filter((item) => item.code === VALIDATION_CODES.policy_floor_breach)
  if (floorBreaches.length < 2) {
    return items
  }
  const first = floorBreaches[0]
  if (!first) {
    return items
  }
  const others = items.filter((item) => item.code !== VALIDATION_CODES.policy_floor_breach)
  let worstShortfall = 0
  let worstMonth = ''
  for (const item of floorBreaches) {
    const shortfallMatch = item.message.match(/shortfall ([\d.]+)/)
    const shortfall = shortfallMatch ? Number(shortfallMatch[1]) : 0
    const monthMatch = item.message.match(/at month (\d{4}-\d{2})/)
    const month = monthMatch?.[1] ?? ''
    if (shortfall > worstShortfall) {
      worstShortfall = shortfall
      worstMonth = month
    }
  }
  return [
    ...others,
    {
      ...first,
      message: `Operating cash below floor in ${floorBreaches.length} months (worst ${formatCurrency(worstShortfall)} in ${worstMonth})`,
    },
  ]
}

const MAPPING_INCOMPLETE_TOOLTIP =
  'Partner mapping incomplete — see `cash_policy.member_partner_map`'

/** Missing/stale schedule errors are resolved on Apply; omit from the modal's live preview strip. */
const MODAL_PREVIEW_EXCLUDED_POLICY_CODES = new Set<string>([
  VALIDATION_CODES.policy_schedule_missing,
  VALIDATION_CODES.policy_schedule_stale,
])

export default function CashPolicyModal({
  open,
  canEdit,
  caseName,
  draft,
  storeDirty,
  schema,
  history,
  model,
  lastClosedMonth,
  onClose,
  onSetCashPolicy,
}: CashPolicyModalProps) {
  const [workingCopy, setWorkingCopy] = useState<CashPolicy | null>(null)
  const [initialCopy, setInitialCopy] = useState<CashPolicy | null>(null)
  const [previewEvaluated, setPreviewEvaluated] = useState<Model | null>(null)
  const [distHistory, setDistHistory] = useState<DistributionHistoryResponse | null>(null)
  const [floorAnalysisWindowMonths, setFloorAnalysisWindowMonths] = useState<FloorAnalysisWindowMonths>(12)
  const [floorAnalysis, setFloorAnalysis] = useState<FloorAnalysisResponse | null>(null)
  const [floorAnalysisLoading, setFloorAnalysisLoading] = useState(false)
  const [cushionDays, setCushionDays] = useState(0)
  const [yieldStale, setYieldStale] = useState(false)
  const [yieldUnavailable, setYieldUnavailable] = useState(false)
  const [splitWarnings, setSplitWarnings] = useState<string[]>([])
  const [splitRefreshing, setSplitRefreshing] = useState(false)
  const [applyError, setApplyError] = useState<string | null>(null)
  const [computingWaterfall, setComputingWaterfall] = useState(false)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const mappingIncomplete =
    model.tier4_shares == null || (model.tier4_warnings?.length ?? 0) > 0
  const mappingWarning = mappingIncomplete
    ? model.tier4_warnings?.length
      ? model.tier4_warnings.join('; ')
      : 'Partner mapping incomplete — configure cash_policy.member_partner_map'
    : null

  const draftPolicy = draft.cash_policy
  const contextPolicy = workingCopy ?? draftPolicy ?? null
  const context = useMemo(
    () => evaluateContextFromModel(model, contextPolicy),
    [model, contextPolicy],
  )

  useEffect(() => {
    if (!open) {
      return
    }
    let cancelled = false
    const init = async () => {
      const historyResp = await getDistributionHistory(12).catch(() => null)
      if (cancelled) return
      setDistHistory(historyResp)
      const base =
        draftPolicy != null
          ? structuredClone(draftPolicy)
          : defaultCashPolicy(model, history, historyResp ?? { items: [], last_date: null, median_gap_days: null, avg_amount_last_6: null })
      setWorkingCopy(base)
      setInitialCopy(structuredClone(base))
      setApplyError(null)
      setSplitWarnings([])
      const settings = cashPolicySettings(model)
      setCushionDays(Number(settings.cushion_days_default ?? 0))
      try {
        const curve = await getYieldCurve()
        if (cancelled) return
        setYieldStale(curve.stale)
        setYieldUnavailable(false)
        setWorkingCopy((prev) =>
          prev
            ? {
                ...prev,
                sweep: {
                  ...prev.sweep,
                  yield_snapshot: {
                    as_of: curve.as_of,
                    source: curve.source,
                    spread: Number(curve.spread),
                    apr_by_month: curve.apr_by_month.map((apr) => Number(apr)),
                  },
                },
              }
            : prev,
        )
        setInitialCopy((prev) =>
          prev
            ? {
                ...prev,
                sweep: {
                  ...prev.sweep,
                  yield_snapshot: {
                    as_of: curve.as_of,
                    source: curve.source,
                    spread: Number(curve.spread),
                    apr_by_month: curve.apr_by_month.map((apr) => Number(apr)),
                  },
                },
              }
            : prev,
        )
      } catch {
        if (cancelled) return
        setYieldUnavailable(true)
        toast.error('Could not load Treasury yield curve')
      }
    }
    void init()
    return () => {
      cancelled = true
    }
  }, [open, draftPolicy, history, model])

  useEffect(() => {
    if (!open || !workingCopy) {
      return
    }
    if (debounceRef.current) {
      clearTimeout(debounceRef.current)
    }
    debounceRef.current = setTimeout(() => {
      const inputs: CaseInputs = { ...structuredClone(draft), cash_policy: workingCopy }
      const evaluated = evaluate(schema, history, inputs, lastClosedMonth, context)
      setPreviewEvaluated(evaluated)
    }, 150)
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
      }
    }
  }, [open, workingCopy, draft, schema, history, lastClosedMonth, context])

  useEffect(() => {
    if (!open) {
      return
    }
    const cached = getCachedFloorAnalysis(floorAnalysisWindowMonths)
    if (cached) {
      setFloorAnalysis(cached)
      setFloorAnalysisLoading(false)
      return
    }
    let cancelled = false
    setFloorAnalysisLoading(true)
    void loadFloorAnalysis(floorAnalysisWindowMonths)
      .then((data) => {
        if (cancelled) return
        setFloorAnalysis(data)
      })
      .catch((error) => {
        toast.error(formatApiError(error, 'Floor analysis failed'))
      })
      .finally(() => {
        if (!cancelled) setFloorAnalysisLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [open, floorAnalysisWindowMonths])

  const unchangedVsDraft = policiesEqual(workingCopy, draftPolicy)
  const unchangedVsInitial = policiesEqual(workingCopy, initialCopy)

  const policyValidation = useMemo(() => {
    if (!previewEvaluated || !workingCopy) {
      return { errors: [], warnings: [], displayItems: [] as ValidationIssue[] }
    }
    const inputs: CaseInputs = { ...structuredClone(draft), cash_policy: workingCopy }
    const result = validateCase(
      previewEvaluated,
      inputs,
      history,
      thresholdsFromSettings(model),
      validationOptionsFromModel(model, contextPolicy),
    )
    const errors = result.errors.filter(
      (item) =>
        item.section === 'Cash policy' && !MODAL_PREVIEW_EXCLUDED_POLICY_CODES.has(item.code),
    )
    const warnings = result.warnings.filter(
      (item) =>
        item.section === 'Cash policy' && !MODAL_PREVIEW_EXCLUDED_POLICY_CODES.has(item.code),
    )
    return {
      errors,
      warnings,
      displayItems: collapseFloorBreachWarnings([...errors, ...warnings]),
    }
  }, [previewEvaluated, workingCopy, draft, history, model, contextPolicy])

  const handleCancel = useCallback(() => {
    if (!unchangedVsInitial && !window.confirm('Discard cash policy changes?')) {
      return
    }
    onClose()
  }, [onClose, unchangedVsInitial])

  useEffect(() => {
    if (!open) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        handleCancel()
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [open, handleCancel])

  const refreshSplit = async () => {
    if (!workingCopy) return
    setSplitRefreshing(true)
    setApplyError(null)
    try {
      const response = await computeWaterfallSchedule(
        waterfallScheduleRequestFromPolicy(workingCopy),
      )
      const schedule = parseApiCashPolicySplitSchedule(response.split_schedule)
      setWorkingCopy((prev) =>
        prev
          ? {
              ...prev,
              distribution: { ...prev.distribution, split_schedule: schedule },
            }
          : prev,
      )
      setSplitWarnings(response.warnings)
    } catch (error) {
      toast.error(formatApiError(error, 'Waterfall schedule failed'))
      setApplyError(formatApiError(error, 'Waterfall schedule failed'))
    } finally {
      setSplitRefreshing(false)
    }
  }

  const hasInvalidSchedule = policyValidation.errors.some(
    (e) => e.code === VALIDATION_CODES.policy_schedule_invalid,
  )
  const hasBlockingValidationErrors = policyValidation.errors.some(
    (e) => e.code !== VALIDATION_CODES.policy_schedule_invalid,
  )
  const applyDisabled =
    !canEdit ||
    mappingIncomplete ||
    hasBlockingValidationErrors ||
    (unchangedVsDraft && !hasInvalidSchedule)
  const applyTooltip = mappingIncomplete
    ? MAPPING_INCOMPLETE_TOOLTIP
    : hasBlockingValidationErrors
      ? 'Fix cash policy validation errors before applying'
      : !canEdit
        ? 'Editing is disabled'
        : unchangedVsDraft
          ? 'No changes to apply'
          : ''

  const handleApply = async () => {
    if (!workingCopy || !canEdit) return
    setApplyError(null)
    const policyForApply = {
      ...workingCopy,
      sweep: { ...workingCopy.sweep, yield_income_view: 'ga' as const },
    }
    const needsWaterfall = !scheduleIsValid(
      policyForApply.distribution.split_schedule,
      policyForApply,
      36,
      context,
    )
    setComputingWaterfall(true)
    try {
      const { policy: policyToApply, problems } = await hydrateCashPolicy(
        policyForApply,
        context,
        {
          computeWaterfallSchedule,
          getYieldCurve,
        },
        { forceWaterfall: hasInvalidSchedule },
      )
      if (!policyToApply) {
        return
      }
      if ((needsWaterfall || hasInvalidSchedule) && !policyToApply.distribution.split_schedule) {
        const message = problems[0] ?? 'Waterfall schedule failed'
        setApplyError(message)
        toast.error(message)
        return
      }
      if (problems.length > 0) {
        for (const problem of problems) {
          toast.error(problem)
        }
      }
      const applyContext = evaluateContextFromModel(model, policyToApply)
      const applyOptions = validationOptionsFromModel(model, policyToApply)
      const inputs = { ...structuredClone(draft), cash_policy: policyToApply }
      const evaluated = evaluate(schema, history, inputs, lastClosedMonth, applyContext)
      const result = validateCase(
        evaluated,
        inputs,
        history,
        thresholdsFromSettings(model),
        applyOptions,
      )
      const cashPolicyError = result.errors.find((e) => e.section === 'Cash policy')
      if (cashPolicyError) {
        setApplyError(cashPolicyError.message)
        toast.error(cashPolicyError.message)
        return
      }
      onSetCashPolicy(policyToApply)
      onClose()
    } catch (error) {
      const message = formatApiError(error, 'Waterfall schedule failed')
      setApplyError(message)
      toast.error(message)
    } finally {
      setComputingWaterfall(false)
    }
  }

  const handleDisable = () => {
    if (!draftPolicy) return
    onSetCashPolicy({ ...draftPolicy, enabled: false })
    onClose()
  }

  if (!open || !workingCopy) {
    return null
  }

  const cashPolicy = previewEvaluated?.cash_policy
  const lastClosedOperating =
    history.values.consolidated?.['bs.cash_bank_accounts_and_petty_cash']?.[history.nHistory - 1]
  const lastClosedMm = history.values.consolidated?.['bs.cash_equivalents']?.[history.nHistory - 1]
  const lastClosedTotal = history.values.consolidated?.['bs.total_cash_and_equivalents']?.[
    history.nHistory - 1
  ]

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4"
      onClick={handleCancel}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="cash-policy-title"
        className="relative flex max-h-[90vh] w-full max-w-6xl flex-col rounded-xl bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex shrink-0 items-start justify-between gap-3 border-b border-gray-200 px-5 py-4">
          <div className="flex min-w-0 flex-wrap items-center gap-3">
            <h3 id="cash-policy-title" className="text-base font-semibold text-gray-900">
              Cash Policy: {caseName}
            </h3>
            {storeDirty ? (
              <span className="rounded bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-900">
                Draft
              </span>
            ) : null}
          </div>
          <Button variant="outline" iconOnly aria-label="Close" onClick={handleCancel}>
            <XMarkIcon className="h-5 w-5" />
          </Button>
        </div>
        <div className="flex min-h-0 flex-1 overflow-hidden">
          <div className="w-96 shrink-0 overflow-y-auto border-r border-gray-200 px-4 py-4">
            <PolicyInputsPanel
              policy={workingCopy}
              draft={draft}
              schema={schema}
              history={history}
              lastClosedMonth={lastClosedMonth}
              context={context}
              model={model}
              distHistory={distHistory}
              policyEnabled={workingCopy.enabled}
              canEditPolicy={canEdit}
              onPolicyEnabledChange={(enabled) => setWorkingCopy({ ...workingCopy, enabled })}
              floorAnalysis={floorAnalysis}
              floorAnalysisLoading={floorAnalysisLoading}
              floorAnalysisWindowMonths={floorAnalysisWindowMonths}
              cushionDays={cushionDays}
              yieldStale={yieldStale}
              yieldUnavailable={yieldUnavailable}
              splitWarnings={splitWarnings}
              splitRefreshing={splitRefreshing}
              canEdit={canEdit}
              mappingWarning={mappingWarning}
              onPolicyChange={setWorkingCopy}
              onFloorAnalysisWindowMonthsChange={setFloorAnalysisWindowMonths}
              onCushionDaysChange={setCushionDays}
              onUseRecommendedFloor={(amount) =>
                setWorkingCopy((prev) =>
                  prev
                    ? {
                        ...prev,
                        floor: { operating_minimum: amount, drawdown_allowance: 0 },
                      }
                    : prev,
                )
              }
              onRefreshSplit={() => void refreshSplit()}
            />
          </div>
          <div className="flex min-w-0 flex-1 flex-col overflow-y-auto px-4 py-4">
            {previewEvaluated ? (
              policyValidation.displayItems.length > 0 ? (
                <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3">
                  <p className="text-xs font-semibold text-red-800">Cash policy validation</p>
                  <ul className="mt-2 space-y-2">
                    {policyValidation.displayItems.map((item, i) => (
                      <li key={`${item.code}-${i}`} className="flex items-start gap-2 text-xs text-red-700">
                        <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-red-500" />
                        <span>{item.message}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : (
                <div className="mb-4 rounded-lg border border-green-200 bg-green-50 px-3 py-2">
                  <p className="text-xs font-medium text-green-700">Cash policy validation passed</p>
                </div>
              )
            ) : null}
            {cashPolicy ? (
              <div className="space-y-6">
                <section>
                  <h4 className="mb-3 text-sm font-medium text-gray-900">Summary</h4>
                  <PolicySummaryTiles
                    summary={cashPolicy.summary}
                    amountPerPeriod={workingCopy.distribution.amount_per_period ?? 0}
                    cadence={workingCopy.distribution.cadence}
                    minOperatingCashMonth={lastClosedMonth.slice(0, 7)}
                    lastClosedMonth={lastClosedMonth}
                  />
                </section>
                <section>
                  <h4 className="mb-3 text-sm font-medium text-gray-900">Projected cash balances</h4>
                  <PolicyChart
                    months={cashPolicy.months}
                    effectiveFloor={cashPolicy.effective_floor}
                    showDistributions
                  />
                </section>
                <section>
                  <h4 className="mb-3 text-sm font-medium text-gray-900">Monthly detail</h4>
                  <PolicyMonthTable
                    months={cashPolicy.months}
                    lastClosedMonth={lastClosedMonth.slice(0, 7)}
                    lastClosedOperatingCash={
                      typeof lastClosedOperating === 'number' ? lastClosedOperating : null
                    }
                    lastClosedMm={typeof lastClosedMm === 'number' ? lastClosedMm : null}
                    lastClosedTotalCash={typeof lastClosedTotal === 'number' ? lastClosedTotal : null}
                  />
                </section>
              </div>
            ) : null}
          </div>
        </div>
        <div className="shrink-0 border-t border-gray-200 px-5 py-4">
          {applyError ? <p className="mb-2 text-sm text-red-600">{applyError}</p> : null}
          <div className="flex flex-wrap justify-end gap-2">
            {draftPolicy?.enabled ? (
              <Button variant="outline" size="md" onClick={handleDisable}>
                Disable policy
              </Button>
            ) : null}
            {applyDisabled && applyTooltip ? (
              <HoverHelp
                label={
                  <Button
                    variant="primary"
                    size="md"
                    disabled
                    onClick={() => undefined}
                  >
                    Apply to case
                  </Button>
                }
                aria-label="Apply to case help"
              >
                {applyTooltip}
              </HoverHelp>
            ) : (
              <Button
                variant="primary"
                size="md"
                disabled={applyDisabled || computingWaterfall}
                onClick={() => void handleApply()}
              >
                {computingWaterfall ? 'Computing waterfall…' : 'Apply to case'}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
